import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  AlertTriangle,
  Building2,
  ChevronRight,
  ClipboardCheck,
  ExternalLink,
  Eye,
  FileText,
  FileWarning,
  Loader2,
  Package,
  Pill,
  Plus,
  RefreshCw,
  Truck,
  Upload,
  UserRound,
} from "lucide-react";
import { toast } from "sonner";
import { useFarmaciaAuth } from "../context/FarmaciaAuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  farmGet,
  farmPatch,
  farmPost,
  STATO_ORDINE_LABELS,
  URGENZA_ORDINE_LABELS,
} from "./farmacia/shared";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const URGENZA_RANK = { critica: 0, urgente: 1, normale: 2 };

const FLOW_COLUMNS = [
  {
    id: "da_verificare",
    label: "Da verificare",
    accent: "bg-rose-500",
    header: "text-rose-700",
    match: (o) =>
      o.stato === "bloccata" ||
      o.terapia_da_visionare ||
      o.stato === "nessuna_pratica",
  },
  {
    id: "in_attesa_documenti",
    label: "In attesa documenti",
    accent: "bg-amber-500",
    header: "text-amber-700",
    match: (o) =>
      o.stato === "attesa_medico" ||
      (o.anomalie_documentali || []).length > 0 ||
      (!o.richiesta_farmacia_ricevuta && o.stato !== "completata" && o.stato !== "nessuna_pratica"),
  },
  {
    id: "in_lavorazione",
    label: "In lavorazione",
    accent: "bg-sky-500",
    header: "text-sky-700",
    match: (o) =>
      (o.carepy_count || 0) > 0 ||
      (o.ricette_count || 0) > 0 ||
      (Boolean(o.richiesta_farmacia_ricevuta) && o.stato !== "pronta_invio" && o.stato !== "completata"),
  },
  {
    id: "pronta_preparazione",
    label: "Pronta per preparazione",
    accent: "bg-emerald-500",
    header: "text-emerald-700",
    match: (o) => o.stato === "pronta_invio",
  },
  {
    id: "completata",
    label: "Completata",
    accent: "bg-slate-400",
    header: "text-slate-600",
    match: (o) => o.stato === "completata",
  },
];

function initials(nome = "", cognome = "") {
  const a = (cognome || "").trim()[0] || "";
  const b = (nome || "").trim()[0] || "";
  const s = `${a}${b}`.toUpperCase();
  return s || "?";
}

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function assignFlowColumn(ospite) {
  for (const col of FLOW_COLUMNS) {
    if (col.id === "completata" && col.match(ospite)) return col.id;
    if (col.id === "pronta_preparazione" && col.match(ospite)) return col.id;
  }
  // priorità: bloccata/visionare → documenti → lavorazione
  if (FLOW_COLUMNS[0].match(ospite)) return "da_verificare";
  if (FLOW_COLUMNS[1].match(ospite)) return "in_attesa_documenti";
  if (FLOW_COLUMNS[3].match(ospite)) return "pronta_preparazione";
  if (FLOW_COLUMNS[2].match(ospite)) return "in_lavorazione";
  if (FLOW_COLUMNS[4].match(ospite)) return "completata";
  return "da_verificare";
}

function OrdineCard({ ordine, busyId, onAction }) {
  const [open, setOpen] = useState(false);
  const [noteFarm, setNoteFarm] = useState(ordine.note_farmacia || "");
  const [righeEdit, setRigheEdit] = useState(() =>
    (ordine.righe || []).map((r) => ({
      ...r,
      quantita_confermata:
        r.quantita_confermata != null ? r.quantita_confermata : r.quantita_richiesta ?? r.quantita ?? 0,
      sost_codice: r.sostituzione_proposta?.prodotto_codice || "",
      sost_nome: r.sostituzione_proposta?.prodotto_nome || "",
      sost_note: r.sostituzione_proposta?.note || "",
    }))
  );
  const busy = busyId === ordine.id;
  const st = STATO_ORDINE_LABELS[ordine.stato] || {
    label: ordine.stato,
    className: "bg-slate-50 text-slate-600 border-slate-200",
  };

  const run = (azione, extra = {}) => {
    const righe = righeEdit.map((r, index) => ({
      index,
      prodotto_codice: r.prodotto_codice,
      quantita_confermata: Number(r.quantita_confermata) || 0,
      quantita_consegnata: r.quantita_consegnata,
      prezzo: r.prezzo,
      sostituzione_proposta:
        r.sost_codice || r.sost_nome
          ? {
              prodotto_codice: r.sost_codice,
              prodotto_nome: r.sost_nome,
              note: r.sost_note || undefined,
            }
          : undefined,
    }));
    onAction(ordine.id, {
      azione,
      note_farmacia: noteFarm.trim() || undefined,
      righe,
      ...extra,
    });
  };

  return (
    <div className="rounded-xl border border-slate-200/80 bg-white shadow-sm">
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 p-4 text-left"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold text-slate-900">
              {ordine.struttura_nome || "Struttura"}
            </span>
            <Badge variant="outline" className={`text-[10px] ${st.className}`}>
              {st.label}
            </Badge>
            {ordine.urgenza && ordine.urgenza !== "normale" ? (
              <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700">
                {URGENZA_ORDINE_LABELS[ordine.urgenza] || ordine.urgenza}
              </Badge>
            ) : null}
          </div>
          <p className="text-xs text-slate-500">
            {(ordine.righe || []).length} righe · {formatWhen(ordine.created_at)}
          </p>
        </div>
        <ChevronRight className={`mt-1 h-4 w-4 shrink-0 text-slate-300 transition ${open ? "rotate-90" : ""}`} />
      </button>

      {open ? (
        <div className="space-y-3 border-t border-slate-100 px-4 pb-4 pt-3">
          {ordine.note ? (
            <p className="text-xs text-slate-500">Note struttura: {ordine.note}</p>
          ) : null}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-left text-xs">
              <thead className="text-slate-500">
                <tr>
                  <th className="pb-1 font-medium">Ospite / CF</th>
                  <th className="pb-1 font-medium">Prodotto</th>
                  <th className="pb-1 font-medium">Rich.</th>
                  <th className="pb-1 font-medium">Conf.</th>
                  <th className="pb-1 font-medium">Cons.</th>
                  <th className="pb-1 font-medium">Prezzo</th>
                  <th className="pb-1 font-medium">Sostituzione</th>
                </tr>
              </thead>
              <tbody>
                {righeEdit.map((r, idx) => (
                  <tr key={idx} className="border-t border-slate-50 align-top">
                    <td className="py-2 pr-2">
                      <div className="font-medium text-slate-800">{r.ospite_nome || "—"}</div>
                      <div className="text-slate-500">{r.codice_fiscale}</div>
                    </td>
                    <td className="py-2 pr-2">
                      <div className="text-slate-800">{r.prodotto_nome}</div>
                      <div className="text-slate-400">{r.prodotto_codice}</div>
                    </td>
                    <td className="py-2 pr-2">{r.quantita_richiesta ?? r.quantita ?? "—"}</td>
                    <td className="py-2 pr-2">
                      <Input
                        type="number"
                        min={0}
                        className="h-8 w-16"
                        value={r.quantita_confermata ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRigheEdit((rows) =>
                            rows.map((row, i) => (i === idx ? { ...row, quantita_confermata: v } : row))
                          );
                        }}
                      />
                    </td>
                    <td className="py-2 pr-2">{r.quantita_consegnata ?? 0}</td>
                    <td className="py-2 pr-2">
                      {r.prezzo != null ? `€ ${Number(r.prezzo).toFixed(2)}` : "—"}
                    </td>
                    <td className="py-2">
                      <Input
                        className="mb-1 h-8"
                        placeholder="Codice sost."
                        value={r.sost_codice}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRigheEdit((rows) =>
                            rows.map((row, i) => (i === idx ? { ...row, sost_codice: v } : row))
                          );
                        }}
                      />
                      <Input
                        className="h-8"
                        placeholder="Nome sost."
                        value={r.sost_nome}
                        onChange={(e) => {
                          const v = e.target.value;
                          setRigheEdit((rows) =>
                            rows.map((row, i) => (i === idx ? { ...row, sost_nome: v } : row))
                          );
                        }}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Input
            placeholder="Note farmacia (opzionale)"
            value={noteFarm}
            onChange={(e) => setNoteFarm(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            {ordine.stato === "inviato" ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => run("marca_ricevuto")}>
                Segna ricevuto
              </Button>
            ) : null}
            {["inviato", "ricevuto", "parziale"].includes(ordine.stato) ? (
              <>
                <Button size="sm" disabled={busy} onClick={() => run("conferma_totale")}>
                  Conferma
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run("conferma_parziale")}>
                  Parziale
                </Button>
                <Button size="sm" variant="outline" disabled={busy} onClick={() => run("proponi_sostituzione")}>
                  Proponi sostituzione
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="text-rose-700"
                  disabled={busy}
                  onClick={() => run("rifiuta")}
                >
                  Rifiuta
                </Button>
              </>
            ) : null}
            {["confermato", "parziale"].includes(ordine.stato) ? (
              <Button size="sm" disabled={busy} onClick={() => run("in_preparazione")}>
                In preparazione
              </Button>
            ) : null}
            {ordine.stato === "in_preparazione" ? (
              <Button size="sm" disabled={busy} onClick={() => run("pronto")}>
                Pronto
              </Button>
            ) : null}
            {ordine.stato === "pronto" ? (
              <Button size="sm" disabled={busy} onClick={() => run("consegnato")}>
                Consegnato
              </Button>
            ) : null}
            {!["consegnato", "annullato", "rifiutato"].includes(ordine.stato) ? (
              <Button
                size="sm"
                variant="ghost"
                className="text-slate-500"
                disabled={busy}
                onClick={() => run("annulla")}
              >
                Annulla
              </Button>
            ) : null}
            {busy ? <Loader2 className="h-4 w-4 animate-spin text-emerald-600" /> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PriorityRow({ icon: Icon, label, value, tone, to, onClick }) {
  const tones = {
    rose: "bg-rose-500/15 text-rose-200",
    sky: "bg-sky-500/15 text-sky-200",
    amber: "bg-amber-500/15 text-amber-200",
    violet: "bg-violet-500/15 text-violet-200",
  };
  const inner = (
    <div className="flex items-center gap-3 rounded-xl px-1 py-2 transition hover:bg-white/5">
      <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${tones[tone] || tones.rose}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1 text-sm text-emerald-50/90">{label}</span>
      <span className="text-lg font-semibold tabular-nums text-white">{value ?? 0}</span>
    </div>
  );
  if (onClick) {
    return (
      <button type="button" className="w-full text-left" onClick={onClick}>
        {inner}
      </button>
    );
  }
  return to ? <Link to={to}>{inner}</Link> : inner;
}

function GuestDetailPanel({ ospite, onClose, onConfirmTerapia }) {
  if (!ospite) {
    return (
      <aside className="hidden w-[300px] shrink-0 xl:block">
        <div className="sticky top-0 rounded-2xl border border-dashed border-slate-200 bg-white/60 p-6 text-center">
          <UserRound className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          <p className="text-sm font-medium text-slate-600">Ospite selezionato</p>
          <p className="mt-1 text-xs text-slate-400">
            Seleziona una card dal flusso pratiche per vedere il dettaglio.
          </p>
        </div>
      </aside>
    );
  }

  const nome = `${ospite.ospite_cognome || ""} ${ospite.ospite_nome || ""}`.trim() || "Ospite";
  const orgId = ospite.organization_id;
  const schedaTo = orgId
    ? `/farmacia/ospite/${encodeURIComponent(ospite.fascicolo_id)}`
    : "/farmacia";

  const docsPending = (ospite.anomalie_documentali || []).length;
  const ricetteMissing = Math.max(
    0,
    (ospite.ricette_count != null ? 0 : 0) +
      (!ospite.ricette_medico_ricevute ? 1 : 0)
  );

  return (
    <aside className="w-full shrink-0 xl:w-[300px]">
      <div className="sticky top-0 flex max-h-[calc(100vh-10rem)] flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="border-b border-slate-100 p-4">
          <div className="flex items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-teal-600 text-sm font-semibold text-white">
              {initials(ospite.ospite_nome, ospite.ospite_cognome)}
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <h3 className="truncate text-sm font-semibold text-slate-900">{nome}</h3>
                <button
                  type="button"
                  className="text-xs text-slate-400 hover:text-slate-600 xl:hidden"
                  onClick={onClose}
                >
                  Chiudi
                </button>
              </div>
              <p className="truncate text-xs text-slate-500">
                {ospite.struttura_nome || ospite.struttura || "Struttura"}
              </p>
              {ospite.codice_fiscale ? (
                <p className="mt-0.5 font-mono text-[10px] text-slate-400">{ospite.codice_fiscale}</p>
              ) : null}
            </div>
          </div>
          <div className="mt-3">
            <Badge
              variant="outline"
              className="border-sky-200 bg-sky-50 text-[10px] text-sky-800"
            >
              {ospite.stato === "completata"
                ? "Completata"
                : ospite.stato === "pronta_invio"
                  ? "Pronta per preparazione"
                  : ospite.stato === "attesa_medico"
                    ? "In attesa documenti"
                    : "In lavorazione"}
            </Badge>
          </div>
        </div>

        <div className="space-y-4 overflow-y-auto p-4">
          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Stato terapia
            </p>
            <div className="grid grid-cols-3 gap-2 text-center">
              <div className="rounded-lg bg-slate-50 px-1 py-2">
                <p className="text-[10px] text-slate-500">Carepy</p>
                <p className="text-[11px] font-semibold text-slate-800">
                  {(ospite.carepy_count || 0) > 0 ? "Caricato" : "Assente"}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 px-1 py-2">
                <p className="text-[10px] text-slate-500">Ricette</p>
                <p className="text-[11px] font-semibold text-slate-800">
                  {ospite.ricette_medico_ricevute
                    ? `${ospite.ricette_count || 0}`
                    : "Mancanti"}
                </p>
              </div>
              <div className="rounded-lg bg-slate-50 px-1 py-2">
                <p className="text-[10px] text-slate-500">Documenti</p>
                <p className="text-[11px] font-semibold text-slate-800">
                  {docsPending > 0 ? `${docsPending} da inviare` : "Ok"}
                </p>
              </div>
            </div>
          </div>
          {ospite.terapia_da_visionare ? (
            <Button size="sm" className="w-full bg-[#0F5C4C] hover:bg-[#0c4a3e]" onClick={() => onConfirmTerapia?.(ospite.fascicolo_id)}>
              <Eye className="mr-1.5 h-3.5 w-3.5" /> Conferma presa visione
            </Button>
          ) : null}

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Documenti ospite
            </p>
            <ul className="space-y-1.5">
              {(ospite.anomalie_documentali || []).length === 0 ? (
                <li className="rounded-lg border border-slate-100 px-2.5 py-2 text-xs text-slate-500">
                  Nessuna anomalia documentale
                </li>
              ) : (
                (ospite.anomalie_documentali || []).map((a) => (
                  <li
                    key={a}
                    className="flex items-center justify-between rounded-lg border border-rose-100 bg-rose-50/50 px-2.5 py-2 text-xs"
                  >
                    <span className="text-slate-700">{String(a).replace(/_/g, " ")}</span>
                    <span className="font-medium text-rose-600">In attesa</span>
                  </li>
                ))
              )}
              {ricetteMissing > 0 && !(ospite.anomalie_documentali || []).length ? (
                <li className="flex items-center justify-between rounded-lg border border-rose-100 bg-rose-50/50 px-2.5 py-2 text-xs">
                  <span className="text-slate-700">Ricette medico</span>
                  <span className="font-medium text-rose-600">In attesa</span>
                </li>
              ) : null}
            </ul>
          </div>

          {(ospite.ordini_aperti || 0) > 0 ? (
            <div>
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                Ordini extra
              </p>
              <div className="rounded-lg border border-slate-100 px-2.5 py-2 text-xs text-slate-600">
                {ospite.ordini_aperti} ordini aperti collegati
              </div>
            </div>
          ) : null}

          <div>
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              Cronologia attività
            </p>
            <ol className="relative space-y-3 border-l border-slate-200 pl-3">
              <li className="text-xs text-slate-600">
                <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-[#0F5C4C]" />
                Pratica {ospite.stato?.replace(/_/g, " ") || "aperta"}
                {ospite.ultima_attivita ? (
                  <span className="mt-0.5 block text-[10px] text-slate-400">
                    {String(ospite.ultima_attivita).slice(0, 16)}
                  </span>
                ) : null}
              </li>
              {ospite.terapia_presente ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-emerald-400" />
                  Terapia presente
                  {ospite.terapia_da_visionare ? " — da visionare" : ""}
                </li>
              ) : null}
              {(ospite.carepy_count || 0) > 0 ? (
                <li className="text-xs text-slate-600">
                  <span className="absolute -left-1 mt-1 h-2 w-2 rounded-full bg-sky-400" />
                  Carepy caricato ({ospite.carepy_count})
                </li>
              ) : null}
            </ol>
          </div>
        </div>

        <div className="mt-auto border-t border-slate-100 p-3">
          <Button asChild className="h-10 w-full rounded-xl bg-[#0F5C4C] hover:bg-[#0c4a3e]">
            <Link to={schedaTo}>
              Vai alla scheda completa
              <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
            </Link>
          </Button>
        </div>
      </div>
    </aside>
  );
}

export default function FarmaciaDashboard() {
  const { token } = useFarmaciaAuth();
  const navigate = useNavigate();
  const [strutture, setStrutture] = useState([]);
  const [ordiniCount, setOrdiniCount] = useState(0);
  const [ordini, setOrdini] = useState([]);
  const [loadingOrdini, setLoadingOrdini] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [ricetteKpi, setRicetteKpi] = useState({});
  const [loadingStrutture, setLoadingStrutture] = useState(true);
  const [loadingKpi, setLoadingKpi] = useState(true);
  const [flusso, setFlusso] = useState([]);
  const [loadingFlusso, setLoadingFlusso] = useState(false);
  const [selected, setSelected] = useState(null);
  const [prossimaConsegna, setProssimaConsegna] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const ordiniRef = useRef(null);

  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadOrdini = useCallback(async () => {
    if (!token) return;
    setLoadingOrdini(true);
    try {
      const res = await farmGet("/farmacia/ordini", token, { aperti: true });
      const items = (res.items || []).slice().sort((a, b) => {
        const ua = URGENZA_RANK[a.urgenza] ?? 9;
        const ub = URGENZA_RANK[b.urgenza] ?? 9;
        if (ua !== ub) return ua - ub;
        return String(b.created_at || "").localeCompare(String(a.created_at || ""));
      });
      setOrdini(items);
      setOrdiniCount(items.length);
    } catch {
      setOrdini([]);
    } finally {
      setLoadingOrdini(false);
    }
  }, [token]);

  const loadFlusso = useCallback(
    async (struttureList) => {
      if (!token || !struttureList?.length) {
        setFlusso([]);
        return;
      }
      setLoadingFlusso(true);
      try {
        const subset = struttureList.slice(0, 6);
        const results = await Promise.all(
          subset.map(async (s) => {
            try {
              const res = await farmGet("/farmacia/ricette/panoramica", token, {
                organization_id: s.organization_id,
              });
              const nome = s.denominazione || s.nome || s.organization_id;
              return (res.tutti_ospiti || []).map((o) => ({
                ...o,
                organization_id: s.organization_id,
                struttura_nome: o.struttura_nome || nome,
              }));
            } catch {
              return [];
            }
          })
        );
        const flat = results.flat();
        setFlusso(flat);
        setSelected((prev) => {
          if (!prev) return flat[0] || null;
          return flat.find((o) => o.fascicolo_id === prev.fascicolo_id) || flat[0] || null;
        });
      } finally {
        setLoadingFlusso(false);
      }
    },
    [token]
  );

  const load = useCallback(async () => {
    if (!token) return;
    setLoadingStrutture(true);
    setLoadingKpi(true);

    const strutturePromise = axios
      .get(`${API_URL}/api/farmacia/strutture`, { headers })
      .then((s) => {
        const items = s.data.items || [];
        setStrutture(items);
        return items;
      })
      .catch(() => {
        setStrutture([]);
        return [];
      })
      .finally(() => setLoadingStrutture(false));

    const kpiPromise = axios
      .get(`${API_URL}/api/farmacia/ricette/kpi`, { headers })
      .then((kpi) => {
        const data = kpi.data || {};
        setRicetteKpi(data);
        if (typeof data.ordini === "number") setOrdiniCount(data.ordini);
      })
      .catch(() => setRicetteKpi({}))
      .finally(() => setLoadingKpi(false));

    const consegnePromise = farmGet("/farmacia/consegne", token, {
      data: new Date().toISOString().slice(0, 10),
    })
      .then((res) => {
        const items = res.items || res.consegne || [];
        const next = items.find((c) =>
          ["da_pianificare", "pianificata", "in_preparazione", "in_consegna", "riprogrammata"].includes(
            c.stato
          )
        );
        setProssimaConsegna(next || items[0] || null);
      })
      .catch(() => setProssimaConsegna(null));

    const [struttureItems] = await Promise.all([
      strutturePromise,
      kpiPromise,
      loadOrdini(),
      consegnePromise,
    ]);
    await loadFlusso(struttureItems);
  }, [token, headers, loadOrdini, loadFlusso]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (window.location.hash === "#ordini" || window.location.hash === "#strutture") {
      const id = window.location.hash.slice(1);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 200);
    }
  }, [loadingStrutture, loadingOrdini]);

  const onOrdineAction = async (ordineId, body) => {
    setBusyId(ordineId);
    try {
      await farmPatch(`/farmacia/ordini/${encodeURIComponent(ordineId)}`, token, body);
      toast.success("Ordine aggiornato");
      await loadOrdini();
      try {
        const kpi = await axios.get(`${API_URL}/api/farmacia/ricette/kpi`, { headers });
        if (typeof kpi.data?.ordini === "number") setOrdiniCount(kpi.data.ordini);
      } catch {
        /* ignore */
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aggiornamento ordine fallito");
    } finally {
      setBusyId(null);
    }
  };

  const goToOrdini = () => navigate("/farmacia/ordini");

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await load();
    } finally {
      setRefreshing(false);
    }
  };

  const flowByCol = useMemo(() => {
    const map = Object.fromEntries(FLOW_COLUMNS.map((c) => [c.id, []]));
    for (const o of flusso) {
      const col = assignFlowColumn(o);
      map[col].push(o);
    }
    return map;
  }, [flusso]);

  const ordiniValore = useMemo(() => {
    let tot = 0;
    for (const o of ordini) {
      for (const r of o.righe || []) {
        if (r.prezzo != null) tot += Number(r.prezzo) * Number(r.quantita_confermata ?? r.quantita_richiesta ?? r.quantita ?? 1);
      }
    }
    return tot;
  }, [ordini]);

  const terapieOrg = ricetteKpi.terapie_da_visionare_org_id;
  const terapieTo = terapieOrg
    ? `/farmacia/struttura/${encodeURIComponent(terapieOrg)}?filtro=da_visionare`
    : strutture[0]?.organization_id
      ? `/farmacia/struttura/${encodeURIComponent(strutture[0].organization_id)}?filtro=da_visionare`
      : "/farmacia";

  const showShell = loadingStrutture && loadingKpi && loadingOrdini;

  const confirmTerapia = async (fascicoloId) => {
    try {
      await farmPost(`/farmacia/ospiti/${encodeURIComponent(fascicoloId)}/terapia/presa-visione`, token);
      setFlusso((prev) => prev.map((item) => item.fascicolo_id === fascicoloId ? { ...item, terapia_da_visionare: false } : item));
      setSelected((prev) => prev?.fascicolo_id === fascicoloId ? { ...prev, terapia_da_visionare: false } : prev);
      setRicetteKpi((prev) => ({ ...prev, terapie_da_visionare: Math.max(0, (prev.terapie_da_visionare || 0) - 1) }));
      toast.success("Terapia visionata");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Conferma presa visione non riuscita");
    }
  };

  if (showShell) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  const praticheDaVerificare =
    (ricetteKpi.terapie_da_visionare || 0) + (ricetteKpi.carepy_da_verificare || 0);

  return (
    <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
      <div className="min-w-0 flex-1 space-y-5">
        <section className="rounded-2xl border border-slate-200/80 bg-white p-3 shadow-sm sm:p-4">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-3 px-1">
            <h3 className="text-sm font-semibold text-slate-800">Azioni rapide</h3>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="rounded-xl"
              onClick={onRefresh}
              disabled={refreshing}
            >
              <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
              Aggiorna dati
            </Button>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm" className="rounded-xl border-slate-200 bg-slate-50">
              <Link to="/farmacia/ospiti">
                <UserRound className="mr-1.5 h-3.5 w-3.5" />
                Cerca scheda paziente
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="rounded-xl border-slate-200 bg-slate-50">
              <Link to="/farmacia/ospiti?filtro=da_verificare">
                <Pill className="mr-1.5 h-3.5 w-3.5" />
                Modifica terapia
              </Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="rounded-xl border-slate-200 bg-slate-50">
              <Link to="/farmacia/richieste"><Upload className="mr-1.5 h-3.5 w-3.5" />Carica Carepy</Link>
            </Button>
            <Button asChild variant="outline" size="sm" className="rounded-xl border-slate-200 bg-slate-50">
              <Link to="/farmacia/pratiche"><ClipboardCheck className="mr-1.5 h-3.5 w-3.5" />Gestisci pratiche</Link>
            </Button>
            <Button type="button" variant="outline" size="sm" className="rounded-xl border-slate-200 bg-slate-50" onClick={goToOrdini}>
              <Package className="mr-1.5 h-3.5 w-3.5" />Gestisci ordini
            </Button>
          </div>
        </section>

        {/* Row 1: Priorità + Flusso */}
        <div className="grid gap-4 xl:grid-cols-[280px_minmax(0,1fr)]">
          <section className="rounded-2xl bg-[#0F5C4C] p-5 text-white shadow-sm">
            <h2 className="text-sm font-semibold tracking-wide text-emerald-50">Priorità di oggi</h2>
            <div className="mt-4 space-y-1">
              <PriorityRow
                icon={Eye}
                label="Pratiche da verificare"
                value={loadingKpi ? "…" : praticheDaVerificare}
                tone="rose"
                to={terapieTo}
              />
              <PriorityRow
                icon={AlertTriangle}
                label="Ricette mancanti"
                value={loadingKpi ? "…" : ricetteKpi.ricette_mancanti}
                tone="sky"
                to="/farmacia/pratiche?vista=copertura"
              />
              <PriorityRow
                icon={FileWarning}
                label="Carepy in ritardo"
                value={loadingKpi ? "…" : ricetteKpi.carepy_in_ritardo}
                tone="rose"
                to="/farmacia/ospiti?filtro=carepy_in_ritardo"
              />
              <PriorityRow
                icon={Package}
                label="Ordini strutture"
                value={loadingKpi || loadingOrdini ? "…" : ordiniCount}
                tone="amber"
                onClick={goToOrdini}
              />
              <PriorityRow
                icon={FileWarning}
                label="Documenti in scadenza"
                value={loadingKpi ? "…" : ricetteKpi.documenti_in_scadenza}
                tone="violet"
                to="/farmacia/pratiche?vista=copertura"
              />
            </div>
            <Link
              to="/farmacia/pratiche"
              className="mt-4 flex items-center justify-center gap-1 rounded-xl bg-white/10 px-3 py-2.5 text-xs font-medium text-emerald-50 transition hover:bg-white/15"
            >
              Vai a tutte le attività
              <ChevronRight className="h-3.5 w-3.5" />
            </Link>
          </section>

          <section className="min-w-0 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm md:p-5">
            <div className="mb-4 flex items-center justify-between gap-2">
              <h2 className="text-sm font-semibold text-slate-800">Flusso pratiche</h2>
              {loadingFlusso ? <Loader2 className="h-4 w-4 animate-spin text-slate-300" /> : null}
            </div>
            <div className="flex gap-3 overflow-x-auto pb-1">
              {FLOW_COLUMNS.map((col) => {
                const items = flowByCol[col.id] || [];
                return (
                  <div key={col.id} className="w-[160px] shrink-0 sm:w-[170px]">
                    <div className="mb-2 flex items-center gap-2">
                      <span className={`h-1.5 w-1.5 rounded-full ${col.accent}`} />
                      <span className={`text-[11px] font-semibold ${col.header}`}>{col.label}</span>
                      <span className="ml-auto text-[11px] tabular-nums text-slate-400">{items.length}</span>
                    </div>
                    <div className="space-y-2">
                      {items.slice(0, 4).map((o) => {
                        const active = selected?.fascicolo_id === o.fascicolo_id;
                        return (
                          <button
                            key={`${o.fascicolo_id}-${o.id}`}
                            type="button"
                            onClick={() => setSelected(o)}
                            className={`w-full rounded-xl border p-2.5 text-left transition ${
                              active
                                ? "border-emerald-300 bg-emerald-50/60 shadow-sm"
                                : "border-slate-100 bg-slate-50/80 hover:border-slate-200 hover:bg-white"
                            }`}
                          >
                            <div className="flex items-center gap-2">
                              <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-600 text-[10px] font-semibold text-white">
                                {initials(o.ospite_nome, o.ospite_cognome)}
                              </span>
                              <div className="min-w-0">
                                <p className="truncate text-xs font-semibold text-slate-800">
                                  {o.ospite_cognome} {o.ospite_nome}
                                </p>
                                <p className="truncate text-[10px] text-slate-400">
                                  {o.struttura_nome || "—"}
                                </p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                      {items.length === 0 ? (
                        <div className="rounded-xl border border-dashed border-slate-150 border-slate-200 px-2 py-6 text-center text-[10px] text-slate-400">
                          Nessuna
                        </div>
                      ) : null}
                      {items.length > 4 ? (
                        <p className="text-center text-[10px] text-slate-400">+{items.length - 4} altre</p>
                      ) : null}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </div>

        {/* Row 2: summary cards */}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <section
            id="strutture"
            className="scroll-mt-24 rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm"
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Strutture collegate</h3>
              <Link to="/farmacia/strutture" className="text-[11px] font-medium text-emerald-700 hover:underline">
                Vedi tutte
              </Link>
            </div>
            {loadingStrutture ? (
              <Loader2 className="h-5 w-5 animate-spin text-slate-300" />
            ) : strutture.length === 0 ? (
              <p className="text-xs text-slate-400">Nessuna struttura collegata</p>
            ) : (
              <ul className="space-y-2">
                {strutture.slice(0, 4).map((s) => (
                  <li key={s.organization_id}>
                    <Link
                      to={`/farmacia/struttura/${encodeURIComponent(s.organization_id)}`}
                      className="flex items-center justify-between gap-2 rounded-lg px-1 py-1 hover:bg-slate-50"
                    >
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium text-slate-800">
                          {s.denominazione || s.nome || s.organization_id}
                        </p>
                        <p className="text-[10px] text-slate-400">
                          {s.ospiti_aderenti ?? 0} ospiti
                        </p>
                      </div>
                      <Badge
                        variant="outline"
                        className={`shrink-0 text-[10px] ${
                          s.stato_collegamento === "attivo"
                            ? "border-emerald-200 text-emerald-700"
                            : "border-slate-200 text-slate-500"
                        }`}
                      >
                        {s.stato_collegamento === "attivo" ? "Attiva" : "Cessata"}
                      </Badge>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Ordini struttura</h3>
              <Package className="h-4 w-4 text-slate-300" />
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-900">
              {loadingOrdini ? "…" : ordiniCount}
            </p>
            <p className="mt-1 text-xs text-slate-500">ordini attivi</p>
            <p className="mt-3 text-sm font-medium text-slate-700">
              {ordiniValore > 0
                ? `€ ${ordiniValore.toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                : "—"}
            </p>
            <button
              type="button"
              onClick={goToOrdini}
              className="mt-2 text-xs font-medium text-emerald-700 hover:underline"
            >
              Apri coda ordini
            </button>
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Consegne pianificate</h3>
              <Truck className="h-4 w-4 text-slate-300" />
            </div>
            <p className="text-3xl font-semibold tracking-tight text-slate-900">
              {loadingKpi ? "…" : ricetteKpi.consegne_programmate ?? 0}
            </p>
            <p className="mt-1 text-xs text-slate-500">consegne</p>
            <p className="mt-3 text-xs text-slate-600">
              Prossima:{" "}
              {prossimaConsegna?.data
                ? `${prossimaConsegna.data}${prossimaConsegna.fascia_oraria ? `, ${prossimaConsegna.fascia_oraria}` : ""}`
                : "nessuna oggi"}
            </p>
            <Link to="/farmacia/consegne" className="mt-2 inline-block text-xs font-medium text-emerald-700 hover:underline">
              Vai alle consegne
            </Link>
          </section>

          <section className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-800">Performance farmacia</h3>
              <ClipboardCheck className="h-4 w-4 text-slate-300" />
            </div>
            <p className="mb-3 text-[11px] text-slate-400">Indicatori operativi attuali</p>
            <ul className="space-y-2.5">
              <li className="flex items-center justify-between text-xs">
                <span className="text-slate-600">Da sollecitare</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {ricetteKpi.sollecitabili ?? 0}
                </span>
              </li>
              <li className="flex items-center justify-between text-xs">
                <span className="text-slate-600">Scorte basse</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {ricetteKpi.scorte_basse ?? 0}
                </span>
              </li>
              <li className="flex items-center justify-between text-xs">
                <span className="text-slate-600">Fatture aperte</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {ricetteKpi.fatture_aperte ?? 0}
                </span>
              </li>
              <li className="flex items-center justify-between text-xs">
                <span className="text-slate-600">Ospiti aderenti</span>
                <span className="font-semibold tabular-nums text-slate-900">
                  {typeof ricetteKpi.ospiti_aderenti === "number"
                    ? ricetteKpi.ospiti_aderenti
                    : strutture.reduce((a, s) => a + (s.ospiti_aderenti || 0), 0)}
                </span>
              </li>
            </ul>
          </section>
        </div>

        {/* Mobile selected guest */}
        {selected ? (
          <div className="xl:hidden">
            <GuestDetailPanel ospite={selected} onClose={() => setSelected(null)} onConfirmTerapia={confirmTerapia} />
          </div>
        ) : null}

        {/* Coda ordini (funzionale) */}
        <section id="ordini" ref={ordiniRef} className="scroll-mt-24 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-slate-800">Coda ordini</h2>
            <span className="text-xs text-slate-400">
              {loadingOrdini ? "…" : `${ordini.length} aperti`}
            </span>
          </div>
          {loadingOrdini ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-600" />
            </div>
          ) : ordini.length === 0 ? (
            <div className="rounded-xl border border-dashed border-slate-200 bg-white px-4 py-10 text-center">
              <Package className="mx-auto mb-3 h-8 w-8 text-slate-300" />
              <p className="text-sm font-medium text-slate-700">Nessun ordine aperto</p>
              <p className="mt-1 text-xs text-slate-400">
                Gli ordini extra blister inviati dalle strutture compaiono qui per conferma e preparazione.
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {ordini.map((o) => (
                <OrdineCard key={o.id} ordine={o} busyId={busyId} onAction={onOrdineAction} />
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Right panel desktop */}
      <div className="hidden xl:block">
        <GuestDetailPanel ospite={selected} onClose={() => setSelected(null)} onConfirmTerapia={confirmTerapia} />
      </div>
    </div>
  );
}
